<template>
    <rkm-form ref="RkmForm">
        <rkm-row>
            <rkm-col>
                <span><strong>Profissinal:</strong></span>
                <p>{{ data.form.medicoResponsavel.nome }}</p>
            </rkm-col>

            <rkm-col>
                <span><strong>Ocupação:</strong></span>
                <p>{{ data.form.medicoResponsavel.ocupacao.codigo }} - {{ data.form.medicoResponsavel.ocupacao.nome }}</p>
            </rkm-col>

            <rkm-col>
                <span><strong>Paciente:</strong></span>
                <p>{{ data.form.paciente.nome }}</p>
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="data.form.cid">
            <rkm-col>
                <span><strong>CID10 Principal:</strong> {{ data.form.cid.codigo }} - {{ data.form.cid.nome }}</span>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-area
                    title="Procedimentos médicos"
                    type="section"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-table
                                :fields="fields.table"
                                :items="data.form.atendimentoMedico.procedimentos"
                                hide-filters
                                hide-pagination
                                tag="div"
                            >
                                <template v-slot:cell(codigo)="row">
                                    <div v-if="row.item.procedimento.codigo">
                                        {{ row.item.procedimento.codigo | formatProcedimento }}
                                    </div>
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-area
                    title="Procedimentos de enfermagem"
                    type="section"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-table
                                :fields="fields.table"
                                :items="data.form.atendimentoTriagem.procedimentos.concat(
                                    data.form.atendimentoEnfermagem.procedimentos,
                                    data.form.procedimentos,
                                    data.model.procedimentos
                                )"
                                hide-filters
                                hide-pagination
                                tag="div"
                            >
                                <template v-slot:cell(codigo)="row">
                                    <div v-if="row.item.procedimento.codigo">
                                        {{ row.item.procedimento.codigo | formatProcedimento }}
                                    </div>
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-col>
        </rkm-row>

        <rkm-row>
            <rkm-col>
                <rkm-area
                    title="Procedimentos de exames de imagem"
                    type="section"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-table
                                :fields="fields.table"
                                :items="data.form.atendimentoExameImagem.procedimentos"
                                hide-filters
                                hide-pagination
                                tag="div"
                            >
                                <template v-slot:cell(codigo)="row">
                                    <div v-if="row.item.procedimento.codigo">
                                        {{ row.item.procedimento.codigo | formatProcedimento }}
                                    </div>
                                </template>
                            </rkm-table>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-col>
        </rkm-row>

        <rkm-area title="Data Hora Finalização">
            <rkm-row class="mt-2">
                <rkm-col>
                    <rkm-form-date-picker
                        v-model="data.model.finalizacao.data"
                        :maxDate="data.form.dataAtual"
                        label="Data Finalização"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-time-picker
                        v-model="data.model.finalizacao.hora"
                        label="Hora Finalização"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <rkm-row>
            <rkm-col>
                <rkm-area
                    title="Desfecho do atendimento"
                    type="section"
                >
                    <rkm-row>
                        <rkm-col>
                            <rkm-form-radio-group
                                @change="handleDesfechoChange"
                                v-model="data.model.finalizacao.desfechoId"
                            >
                                <rkm-form-radio
                                    :value="enums.desfecho.ALTA_MEDICA"
                                    disabled
                                >
                                    Alta médica
                                </rkm-form-radio>
                                <rkm-form-radio
                                    :value="enums.desfecho.EVASAO"
                                    :disabled="data.model.finalizacao.hasPreviousDesfecho"
                                >
                                    Evasão
                                </rkm-form-radio>
                                <rkm-form-radio
                                    :value="enums.desfecho.ENCAMINHAMENTO_HOSPITALAR"
                                    :disabled="data.model.finalizacao.hasPreviousDesfecho"
                                >
                                    Transferência / Encaminhamento hospitalar
                                </rkm-form-radio>
                                <rkm-form-radio
                                    :value="enums.desfecho.OBITO"
                                    disabled
                                >
                                    Óbito
                                </rkm-form-radio>
                            </rkm-form-radio-group>
                        </rkm-col>
                    </rkm-row>
                </rkm-area>
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="data.model.finalizacao.desfechoId == enums.desfecho.ALTA_MEDICA">
            <rkm-col>
                <rkm-form-select
                    :options="data.form.profissionaisAtendimento"
                    :validations="validations.altaMedica.usuarioId"
                    label="Médico"
                    show-default-option
                    text-field="nome"
                    v-model="data.model.finalizacao.altaMedica.lotacaoId"
                    value-field="lotacaoId"
                />
            </rkm-col>
        </rkm-row>

        <rkm-row v-if="data.model.finalizacao.desfechoId == enums.desfecho.EVASAO">
            <rkm-col>
                <rkm-form-textarea
                    :validations="validations.evasao.motivo"
                    label="Motivo da evasão"
                    v-model="data.model.finalizacao.evasao.motivo"
                />
            </rkm-col>
        </rkm-row>

        <template v-if="data.model.finalizacao.desfechoId == enums.desfecho.ENCAMINHAMENTO_HOSPITALAR">
            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        v-if="data.model.finalizacao.encaminhamentoHospitalar.unidadeOutra === false"
                        :options="data.form.unidades"
                        :validations="validations.encaminhamentoHospitalar.unidadeId"
                        label="Unidade de encaminhamento"
                        text-field="nomeFantasia"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.unidadeId"
                        value-field="id"
                    >
                        <rkm-form-select-option
                            :value="null"
                            slot="first"
                        >
                            Selecione
                        </rkm-form-select-option>
                    </rkm-form-select>

                    <rkm-form-input
                        v-else
                        :validations="validations.encaminhamentoHospitalar.unidadeNome"
                        label="Nome"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.unidadeNome"
                    />
                </rkm-col>

                <rkm-col
                    class="pt-2"
                    cols="2"
                >
                    <div class="d-flex mt-4">
                        <rkm-form-checkbox
                            inline
                            v-model="data.model.finalizacao.encaminhamentoHospitalar.unidadeOutra"
                        >
                            Outra
                        </rkm-form-checkbox>
                    </div>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.encaminhamentoHospitalar.quarto"
                        label="Quarto"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.quarto"
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-input
                        :validations="validations.encaminhamentoHospitalar.leito"
                        label="Leito"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.leito"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        label="Médico hospitalar"
                        :validations="validations.encaminhamentoHospitalar.medicoHospitalar"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.medicoHospitalar"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input-autocomplete
                        :searchFields="fields.autocomplete.encaminhamentoHospitalar.cid"
                        :repository="repositories.cid"
                        :validations="validations.encaminhamentoHospitalar.cidId"
                        label="CID10"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.cid"
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.encaminhamentoHospitalar.observacoes"
                        label="Observações"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.observacoes"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        :options="data.form.profissionaisAtendimento"
                        label="Médico"
                        text-field="nome"
                        v-model="data.model.finalizacao.encaminhamentoHospitalar.lotacaoId"
                        value-field="lotacaoId"
                    >
                        <rkm-form-select-option
                            :value="null"
                            slot="first"
                        >
                            Selecione
                        </rkm-form-select-option>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>
        </template>

        <template v-if="data.model.finalizacao.desfechoId == enums.desfecho.OBITO">
            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Paciente"
                        v-model="data.form.paciente.nome"
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-select
                        :options="data.form.profissionaisAtendimento"
                        label="Médico responsável"
                        text-field="nome"
                        v-model="data.model.finalizacao.obito.lotacaoId"
                        value-field="lotacaoId"
                        disabled
                    >
                        <rkm-form-select-option
                            :value="null"
                            slot="first"
                        >
                            Selecione
                        </rkm-form-select-option>
                    </rkm-form-select>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-date-picker
                        :validations="validations.obito.dataObito"
                        :max-date="data.form.dataAtual"
                        label="Data de óbito"
                        v-model="data.model.finalizacao.obito.dataObito"
                        disabled
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-time-picker
                        :validations="validations.obito.horaObito"
                        label="Hora de óbito"
                        v-model="data.model.finalizacao.obito.horaObito"
                        disabled
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        :validations="validations.obito.declaracao"
                        label="Número da declaração de óbito"
                        v-model="data.model.finalizacao.obito.declaracao"
                        disabled
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-input-autocomplete
                        :searchFields="fields.autocomplete.obito.cid"
                        :repository="repositories.cid"
                        :validations="validations.obito.cidId"
                        label="CID10"
                        placeholder="CID10 referente a causa do óbito"
                        v-model="data.model.finalizacao.obito.cid"
                        disabled
                    >
                        <template v-slot:itemLabel="{ item }">
                            {{ item.codigo | formatCid }} - {{ item.nome }}
                        </template>
                    </rkm-form-input-autocomplete>
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col>
                    <rkm-form-textarea
                        :validations="validations.obito.causa"
                        label="Causa do óbito"
                        v-model="data.model.finalizacao.obito.causa"
                        disabled
                    />
                </rkm-col>
            </rkm-row>
        </template>

        <modal-impressao
            from-observacao
            ref="ModalImpressao"
        />

        <ModalAssinaturaEletronica
            :after-sign="afterSign"
            :ref="modalAssinaturaEletronicaRefName"
            @sign="onSign"
            @skip-sign="onSkipSign"
            v-bind="modalAssinaturaEletronicaFaaProps"
        />
    </rkm-form>
</template>

<script>
// Components.
import ModalImpressao from '@/components/pronto-atendimento/impressao/components/impressao/Modal'

// Enums.
import desfecho from '@/enums/pronto-atendimento/desfecho'
import documentoTipo from '@/enums/cadastro/documentoTipo'
import tipoAtendimento from '@/enums/pronto-atendimento/parametrizacao/tipoAtendimento'

// Filters.
import formats from '@/filters/formats'

// Mixins.
import { error, repository, unmask, stringCase, object, prontoAtendimentoAssinaturaEletronica } from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import { mapActions } from 'vuex'

export default {
    components: { ModalImpressao },
    computed: {
        validations() {
            return {
                altaMedica: {
                    usuarioId: { required: true }
                },
                evasao: {
                    motivo: { required: true, min: 1, max: 200 },
                },
                encaminhamentoHospitalar: {
                    unidadeId: { required: true },
                    unidadeNome: { required: true, max: 150 },
                    quarto: { max: 3 },
                    leito: { max: 3 },
                    cidId: { required: true },
                    medicoHospitalar: { max: 150, name: true },
                    observacoes: { max: 3000 },
                },
                obito: {
                    dataObito: { required: true },
                    horaObito: { required: true },
                    declaracao: { required: true, numeric: true, min: 8, max: 13 },
                    causa: { required: true, min: 1, max: 250 },
                    cidId: { required: true },
                },
            }
        },
    },
    created() {
        this.repositories.pacienteObservacao = this.getConectaSusRepository('prontoAtendimento.observacao')
        this.repositories.cid = this.getConectaSusRepository('sigtap.cid')
    },
    data: () => ({
        enums: { desfecho, documentoTipo, tipoAtendimento },
        fields: {
            autocomplete: {
                obito: {
                    cid: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                },
                encaminhamentoHospitalar: {
                    cid: [
                        { key: 'codigo', searchable: true },
                        { key: 'nome', searchable: true },
                    ],
                },
            },
            table: [
                { key: 'lotacao.ocupacao.codigo', label: 'CBO' },
                { key: 'lotacao.profissional.nome', label: 'Profissional' },
                { key: 'codigo', label: 'Código' },
                { key: 'procedimento.nome', label: 'Procedimento' },
            ]
        },
        repositories: {},
    }),
    filters: {
        ...formats,
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        handleDesfechoChange() {
            this.resetDesfecho()

            if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.ENCAMINHAMENTO_HOSPITALAR) {
                this.data.model.finalizacao.encaminhamentoHospitalar.cid = Object.assign({}, this.data.model.cid)
            }
            else if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.OBITO) {
                this.data.model.finalizacao.obito.cid = Object.assign({}, this.data.model.cid)
            }
        },
        save() {
            this.$refs.RkmForm.validate().then(response => {
                if (response === true) {
                    if (this.data.model.classificacaoRiscoId === null) {
                        this.createNotification({
                            type: 'alert',
                            color: 'danger',
                            title: 'Ação não permitida',
                            message: 'É obrigatório informar a classificação de risco.'
                        })
                    }
                    else {
                        this.setLoading()

                        let request = {
                            plantaoId: this.plantaoId,
                            atendimentoId: this.data.form.atendimentoId,
                            classificacaoRiscoId: this.data.model.classificacaoRiscoId,
                            aguardandoVagaHospitalar: this.data.model.aguardandoVagaHospitalar ? true : false,
                            quarto: this.data.model.quarto,
                            leito: this.data.model.leito,
                            alergias: this.data.model.alergias.map(item => {
                                return {
                                    alergiaId: item.alergia.id,
                                    criticidadeId: item.alergia.criticidade.id,
                                    reacoesObservacoes: item.alergia.reacoesObservacoes,
                                }
                            }),
                            medicamentosUso: this.data.model.medicamentosUso,
                            sinaisVitais: this.data.model.sinaisVitais.map(item => {
                                return {
                                    pa: item.pa || null,
                                    paUnidadeMedidaId: item.pa ? item.paUnidadeMedidaId : null,
                                    glicemia: item.glicemia || null,
                                    temperatura: item.temperatura ? this.unMaskTemperaturaMMDG(item.temperatura) : null,
                                    pulso: item.pulso || null,
                                    respiracao: item.respiracao || null,
                                    saturacao: item.saturacao || null,
                                    intensidadeDor: item.intensidadeDor || null,
                                    cintura: item.cintura || null,
                                }
                            }),
                            gestacao: {
                                gestante: this.data.model.gestacao.gestante,
                                dum: this.data.model.gestacao.dum ? moment(this.data.model.gestacao.dum).format('DD/MM/YYYY') : null,
                            },
                            anotacoes: this.data.model.anotacoes.map(item => {
                                return {
                                    anotadoEm: item.dataHoraAnotacao,
                                    descricao: item.anotacao
                                }
                            }),
                            fatoresRisco: this.data.model.fatoresRisco,
                            fatorRiscoOutro: this.data.model.fatorRiscoOutro,
                            avaliacao: this.data.model.avaliacao,
                            planejamento: this.data.model.planejamento,
                            implementacao: this.data.model.implementacao,
                            historico: this.data.model.historico,
                            diagnostico: this.data.model.diagnostico,
                            procedimentos: this.data.model.procedimentos.map(item => {
                                return {
                                    procedimentoId: item.procedimento.id,
                                    cidPrincipalId: item.cidPrincipal ? item.cidPrincipal.id : null,
                                    quantidade: item.quantidade,
                                }
                            }),
                            servicosEnfermagem: this.data.model.servicosEnfermagem.map(item => {
                                return {
                                    id: isNaN(item.id) ? null : item.id,
                                    statusId: item.status.id,
                                    procedimentoId: item.procedimento.id,
                                    cidId: item.cid ? item.cid.id : null,
                                    horarioRealizacao: item.horarioRealizacao,
                                    observacoes: item.observacoes,
                                }
                            }),
                            hemorragiaExternaGraveTraumaStatusId: this.data.model.hemorragiaExternaGraveTraumaStatusId,
                            hemorragiaExternaGraveLocal: this.data.model.hemorragiaExternaGraveLocal,
                            viasAeriasTraumaStatusId: this.data.model.viasAeriasTraumaStatusId,
                            viasAeriasObstrucaoPor: this.data.model.viasAeriasObstrucaoPor,
                            oxigenacaoRespiracaoVentilacaoTraumaStatusId: this.data.model.oxigenacaoRespiracaoVentilacaoTraumaStatusId,
                            pulsoTraumaStatusId: this.data.model.pulsoTraumaStatusId,
                            peleTraumaStatusId: this.data.model.peleTraumaStatusId,
                            peleAlteracao: this.data.model.peleAlteracao,
                            perfuracaoTraumaStatusId: this.data.model.perfuracaoTraumaStatusId,
                            perfuracaoLentificada: this.data.model.perfuracaoLentificada,
                            sangramentoTraumaStatusId: this.data.model.sangramentoTraumaStatusId,
                            sangramentoLocal: this.data.model.sangramentoLocal,
                            pupilasTraumaStatusId: this.data.model.pupilasTraumaStatusId,
                            paresiasTraumaStatusId: this.data.model.paresiasTraumaStatusId,
                            plegiaTraumaStatusId: this.data.model.plegiaTraumaStatusId,
                            exposicaoHipotermiaTraumaStatusId: this.data.model.exposicaoHipotermiaTraumaStatusId,
                            exposicaoHipotermiaObservacao: this.data.model.exposicaoHipotermiaObservacao,
                            avaliacaoCabecaPescoco: this.data.model.avaliacaoCabecaPescoco.avaliacaoCabecaPescoco,
                            avaliacaoTorax: this.data.model.avaliacaoTorax,
                            avaliacaoAbdome: this.data.model.avaliacaoAbdome,
                            avaliacaoMmssMmii: this.data.model.avaliacaoMmssMmii,
                            observacao: this.data.model.observacao,
                            escalaComaGlasgow: this.data.model.escalasComaGlasgow.map(item => {
                                return {
                                    aberturaOcularId: item.aberturaOcular.id,
                                    respostaVerbalId: item.respostaVerbal.id,
                                    respostaMotoraId: item.respostaMotora.id,
                                    reatividadePupilarId : item.reatividadePupilar.id,
                                    observacao: item.observacoes
                                }
                            })
                        }

                        request.prescricaoInternaAplicacoes = []

                        this.data.model.prescricoesInterna.forEach(prescricao => {

                            prescricao.medicamentos.forEach(medicamento => {

                                medicamento.aplicacoes.filter(a => !a.disabled).forEach(aplicacao => {

                                    request.prescricaoInternaAplicacoes.push({
                                        id: aplicacao.id,
                                        statusId: aplicacao.status.id,
                                        horarioAplicacao: aplicacao.horarioAplicacao,
                                        localAplicacaoId: aplicacao.localAplicacaoId,
                                    })
                                })
                            })
                        })

                        if (this.data.model.avaliacaoAntropometrica.massa || this.data.model.avaliacaoAntropometrica.altura) {
                            request.avaliacaoAntropometrica = {
                                massa: this.data.model.avaliacaoAntropometrica.massa,
                                massaUnidadeMedidaId: this.data.model.avaliacaoAntropometrica.massaUnidadeMedidaId,
                                alturaTipoMedidaId: this.data.model.avaliacaoAntropometrica.alturaTipoMedidaId,
                                altura: this.data.model.avaliacaoAntropometrica.altura,
                                alturaUnidadeMedidaId: this.data.model.avaliacaoAntropometrica.alturaUnidadeMedidaId,
                            }
                        }

                        request.pedidosExames = this.data.model.pedidosExames
                            .map(item => {
                                let exame = item.exames
                                    .map(exame => {
                                        const data = {
                                            id: exame.id,
                                            exameId: exame.exame.id,
                                            observacao: exame.observacao || null,
                                            statusId: exame.status.id,
                                            horarioRealizacao: exame.horarioRealizacao,
                                        }

                                        if(!item.id) {
                                            data.dataSolicitacao = moment(item.dataSolicitacao).format('DD/MM/YYYY')
                                            data.unidadeSolicitante = item.unidadeSolicitante
                                            data.medicoSolicitante = item.medicoSolicitante
                                        }

                                        return data
                                    })

                                return {
                                    isForm: item.isForm,
                                    id: item.id,
                                    urgente: item.urgente,
                                    justificativa: item.justificativa || null,
                                    exames: exame,
                                    files: item.files.filter(f => (f instanceof File)),
                                }
                            })

                        if (this.data.model.finalizacao.desfechoId) {
                            request.finalizacao = {
                                desfecho_id: this.data.model.finalizacao.desfechoId,
                            }

                            if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.ALTA_MEDICA) {
                                request.finalizacao = Object.assign(request.finalizacao , {
                                    alta_medica: {
                                        lotacao_id: this.data.model.finalizacao.altaMedica.lotacaoId,
                                    }
                                })
                            }
                            else if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.EVASAO) {
                                request.finalizacao = Object.assign(request.finalizacao , {
                                    evasao: {
                                        motivo: this.data.model.finalizacao.evasao.motivo,
                                    }
                                })
                            }
                            else if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.ENCAMINHAMENTO_HOSPITALAR) {
                                request.finalizacao = Object.assign(request.finalizacao , {
                                    encaminhamentoHospitalar: {
                                        lotacaoId: this.data.model.finalizacao.encaminhamentoHospitalar.lotacaoId,
                                        unidadeId: this.data.model.finalizacao.encaminhamentoHospitalar.unidadeId,
                                        unidadeNome: this.data.model.finalizacao.encaminhamentoHospitalar.unidadeNome,
                                        quarto: this.data.model.finalizacao.encaminhamentoHospitalar.quarto,
                                        leito: this.data.model.finalizacao.encaminhamentoHospitalar.leito,
                                        cidId: this.data.model.finalizacao.encaminhamentoHospitalar.cid.id,
                                        medicoHospitalar: this.data.model.finalizacao.encaminhamentoHospitalar.medicoHospitalar,
                                        observacoes: this.data.model.finalizacao.encaminhamentoHospitalar.observacoes,
                                    },
                                })
                            }
                            else if (this.data.model.finalizacao.desfechoId === this.enums.desfecho.OBITO) {
                                request.finalizacao = Object.assign(request.finalizacao , {
                                    obito: {
                                        obitoEm: moment(
                                            moment(
                                                this.data.model.finalizacao.obito.dataObito).format('DD/MM/YYYY') + ' ' +
                                                this.data.model.finalizacao.obito.horaObito,
                                                'DD/MM/YYYY HH:mm'
                                            ).format('DD/MM/YYYY HH:mm'),
                                        declaracao: this.data.model.finalizacao.obito.declaracao,
                                        causa: this.data.model.finalizacao.obito.causa,
                                        cidId: this.data.model.finalizacao.obito.cid.id,
                                        lotacaoId: this.data.model.finalizacao.obito.lotacaoId,
                                    },
                                })
                            }
                        }

                        request.finalizacao.dataHora = moment(
                            moment(
                                this.data.model.finalizacao.data).format('DD/MM/YYYY') + ' ' +
                                this.data.model.finalizacao.hora,
                                'DD/MM/YYYY HH:mm'
                            ).format('DD/MM/YYYY HH:mm')

                        const formData = this.objectToFormData(this.camelObjectKeysToSnakeRecursive(request))

                        this.repositories.pacienteObservacao.store(formData)
                            .then(({ data }) => {

                                const finish = () => {

                                    this.$refs.ModalImpressao.open(
                                        data.impressoes,
                                        data.atendimentoId,
                                        () => this.$emit('save')
                                    )
                                }

                                if (data.assinar) {

                                    this.afterSign = faaAssinada => {

                                        return this.repositories.pacienteObservacao.storeFaaAssinada(data.id, {
                                            faa_assinada: faaAssinada
                                        })
                                    }

                                    this.onSign = () => finish()
                                    this.onSkipSign = () => finish()

                                    this.askToSignAtendimento(
                                        async () => this.signFaa(
                                            data.atendimentoId,
                                            [{ tipo: 'observacao', id: data.id }],
                                            `faa-${data.faaId}-atendimento-observacao-${data.id}`
                                        ),
                                        async () => finish()
                                    )
                                }
                                else {

                                    this.showSaveNotification()

                                    finish()
                                }
                            })
                            .catch(this.handleError)
                            .finally(() => this.unsetLoading())
                    }
                }
            })
        },
        resetDesfecho() {
            this.data.model.finalizacao = Object.assign(this.data.model.finalizacao, {
                evasao: {
                    motivo: null,
                },
                encaminhamentoHospitalar: {
                    unidadeId: null,
                    unidadeOutra: false,
                    unidadeNome: null,
                    quarto: null,
                    leito: null,
                    cid: null,
                    medicoHospitalar: null,
                    observacoes: null,
                },
                obito: {
                    dataObito: null,
                    horaObito: null,
                    obitoEm: null,
                    declaracao: null,
                    causa: null,
                    cid: null,
                },
            })
        },
    },
    mixins: [error, repository, unmask, stringCase, object, prontoAtendimentoAssinaturaEletronica],
    props: {
        data: { type: Object, required: true },
        plantaoId: { type: [String, Number], required: true },
    },
    watch: {
        'data.model.finalizacao.encaminhamentoHospitalar.unidadeOutra'(value) {
            if (value === true) {
                this.data.model.finalizacao.encaminhamentoHospitalar.unidadeId = null
            } else {
                this.data.model.finalizacao.encaminhamentoHospitalar.unidadeNome = null
            }
        }
    }
}
</script>